<template>
  <el-dialog 
    title="请假状态异常检查"
    :visible.sync="dialogVisible"
    width="920px"
    :close-on-click-modal="false"
    @open="handleOpen"
  >
    <div class="check-toolbar">
      <span class="check-tip">仅列出主表状态与审批环节不一致的记录（如仍「审批中」但环节已全部通过）。修复为「已批准」时会补跑考勤/天波（已同步跳过）。</span>
      <div>
        <el-button size="small" :loading="loading" @click="loadData">刷新</el-button>
        <el-button
          type="warning"
          size="small"
          :loading="batchFixing"
          :disabled="!list.length"
          @click="handleFixAll"
        >
          全部修复
        </el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      :data="list"
      max-height="420"
      style="width: 100%"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="45" />
      <el-table-column prop="SourceLabel" label="来源" width="90" />
      <el-table-column prop="ApplicantName" label="申请人" width="90" show-overflow-tooltip />
      <el-table-column prop="TargetName" label="被请假人" width="90" show-overflow-tooltip />
      <el-table-column prop="DepartmentName" label="部门" min-width="110" show-overflow-tooltip />
      <el-table-column prop="LeaveTypeName" label="类型" width="80" show-overflow-tooltip />
      <el-table-column label="当前状态" width="90">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.CurrentStatus)" size="mini">
            {{ getStatusText(scope.row.CurrentStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="应有状态" width="90">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.ExpectedStatus)" size="mini">
            {{ getStatusText(scope.row.ExpectedStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="环节号" width="88" align="center">
        <template slot-scope="scope">
          <span :class="{ 'step-mismatch': scope.row.CurrentStepNumber !== scope.row.ExpectedStepNumber }">
            {{ scope.row.CurrentStepNumber }} → {{ scope.row.ExpectedStepNumber }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="StepSummary" label="环节进度" width="100" />
      <el-table-column prop="AnomalyReason" label="异常说明" min-width="180" show-overflow-tooltip />
      <el-table-column label="操作" width="110" fixed="right">
        <template slot-scope="scope">
          <el-button
            type="text"
            size="small"
            :loading="fixingId === buildRowKey(scope.row)"
            @click="handleFixOne(scope.row)"
          >
            修复
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="check-pagination">
      <el-pagination
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="page"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50]"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button
        type="primary"
        :loading="batchFixing"
        :disabled="!multipleSelection.length"
        @click="handleFixSelected"
      >
        修复选中（{{ multipleSelection.length }}）
      </el-button>
      <el-button @click="dialogVisible = false">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  getLeaveStatusAnomalies,
  fixLeaveStatusAnomaly,
  fixLeaveStatusAnomalies
} from '../../../api/modules/misc';

export default {
  name: 'LeaveStatusCheckDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    source: {
      type: String,
      default: 'all'
    },
    departmentId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      batchFixing: false,
      fixingId: '',
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      multipleSelection: []
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      }
    }
  },
  methods: {
    handleOpen() {
      this.page = 1;
      this.loadData();
    },
    loadData() {
      this.loading = true;
      getLeaveStatusAnomalies({
        source: this.source,
        page: this.page,
        intPageSize: this.pageSize,
        departmentId: this.departmentId || ''
      }).then(res => {
        const body = res.data || {};
        if (!body.success) {
          this.$message.error(body.msg || '检查失败');
          this.list = [];
          this.total = 0;
          return;
        }
        const pageModel = body.response || {};
        this.list = pageModel.data || [];
        this.total = pageModel.dataCount || 0;
        if (this.total === 0) {
          this.$message.success('未发现状态异常记录');
        }
      }).catch(() => {
        this.$message.error('检查请求失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    handlePageChange(val) {
      this.page = val;
      this.loadData();
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.page = 1;
      this.loadData();
    },
    handleSelectionChange(val) {
      this.multipleSelection = val || [];
    },
    buildRowKey(row) {
      return `${row.IsAgent ? 1 : 0}_${row.Id}`;
    },
    buildFixPayload(rows) {
      return (rows || []).map(row => ({
        Id: row.Id,
        IsAgent: !!row.IsAgent
      }));
    },
    handleFixOne(row) {
      this.$confirm('确认修复该条记录的状态？', '提示', { type: 'warning' }).then(() => {
        this.fixingId = this.buildRowKey(row);
        fixLeaveStatusAnomaly({ Id: row.Id, IsAgent: !!row.IsAgent }).then(res => {
          if (res.data && res.data.success) {
            this.$message.success(res.data.msg || '修复成功');
            this.$emit('fixed');
            this.loadData();
          } else {
            this.$message.error((res.data && res.data.msg) || '修复失败');
          }
        }).catch(() => {
          this.$message.error('修复请求失败');
        }).finally(() => {
          this.fixingId = '';
        });
      }).catch(() => {});
    },
    handleFixSelected() {
      if (!this.multipleSelection.length) {
        this.$message.warning('请先选择要修复的记录');
        return;
      }
      this.runBatchFix(this.multipleSelection, '选中');
    },
    handleFixAll() {
      if (!this.list.length) return;
      this.$confirm(`确认修复当前页全部 ${this.list.length} 条异常记录？`, '提示', { type: 'warning' })
        .then(() => this.runBatchFix(this.list, '当前页全部'))
        .catch(() => {});
    },
    runBatchFix(rows, label) {
      this.batchFixing = true;
      fixLeaveStatusAnomalies(this.buildFixPayload(rows)).then(res => {
        if (res.data && res.data.success) {
          const result = res.data.response || {};
          this.$message.success(result.Message || res.data.msg || `${label}修复完成`);
          this.$emit('fixed');
          this.loadData();
        } else {
          this.$message.error((res.data && res.data.msg) || '批量修复失败');
        }
      }).catch(() => {
        this.$message.error('批量修复请求失败');
      }).finally(() => {
        this.batchFixing = false;
      });
    },
    getStatusType(status) {
      const typeMap = {
        Pending: 'warning',
        InProgress: 'info',
        Approved: 'success',
        Rejected: 'danger',
        Cancel: 'info',
        Ended: 'success'
      };
      return typeMap[status] || 'default';
    },
    getStatusText(status) {
      const statusMap = {
        Pending: '待审批',
        InProgress: '审批中',
        Approved: '已批准',
        Rejected: '已拒绝',
        Cancel: '已取消',
        Ended: '已结束'
      };
      return statusMap[status] || status || '-';
    }
  }
};
</script>

<style scoped>
.check-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.check-tip {
  color: #909399;
  font-size: 13px;
  max-width: 68%;
  line-height: 1.4;
}

.step-mismatch {
  color: #e6a23c;
  font-weight: 600;
}

.check-pagination {
  margin-top: 12px;
  text-align: right;
}
</style>
